import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePrintRows } from 'hooks/usePrintRows';
import { ApplicationState } from 'store';
import { AiManagementState } from 'store/modules/ai_management_store/types';
import { getColumns, getCards, getTeam } from 'store/modules/ai_management_store/actions';
import { archiveCards, moveCard } from 'services/actions/ai_management_cards';
import Pagination from 'components/atoms/pagination';
import ItemShare from 'components/atoms/item_share';
import AiManagementCardMenu from 'components/molecules/ai_management_card_menu';
import AiManagementListRow from 'components/molecules/ai_management_list_row';
import AiManagementColumnPickerModal from 'components/molecules/ai_management_column_picker_modal';
import AiManagementMembersBulkModal from 'components/molecules/ai_management_members_bulk_modal';
import AiManagementArchiveBulkModal from 'components/molecules/ai_management_archive_bulk_modal';
import { Container } from './styles';

const PAGE_SIZE = 20;

const AiManagementList: React.FC = () => {
  const dispatch = useDispatch();
  const { columns, cards, filters, request } = useSelector<ApplicationState, AiManagementState>(
    (state) => state.session.ai_management,
  );
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showShareWindow, setShowShareWindow] = useState(false);
  const [page, setPage] = useState(1);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [columnModalOpen, setColumnModalOpen] = useState(false);
  const [movingColumn, setMovingColumn] = useState(false);
  const { printRef, printingSelection, print } = usePrintRows({
    documentTitle: 'Gestão IA - Minhas Licitações',
    hasSelection: selected.size > 0,
  });

  useEffect(() => {
    dispatch(getTeam());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getColumns(filters));
    dispatch(getCards(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const rows = useMemo(() => {
    const orderedColumns = [...columns].sort(
      (a, b) => a.sort_group - b.sort_group || a.position - b.position,
    );
    return orderedColumns.flatMap((column) =>
      (cards[column.id] ?? []).map((card) => ({
        card,
        status: column.name,
        statusColor: column.color,
      })),
    );
  }, [columns, cards]);

  const selectedCards = rows.filter((row) => selected.has(row.card.id)).map((row) => row.card);

  const targetColumns = useMemo(
    () =>
      [...columns]
        .filter((column) => column.permissions.accepts_cards)
        .sort((a, b) => a.sort_group - b.sort_group || a.position - b.position),
    [columns],
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pageIds = pageRows.map((row) => row.card.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const visibleRows = printingSelection
    ? rows.filter((row) => selected.has(row.card.id))
    : pageRows;

  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllPage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  const handleChangeColumn = (columnId?: number) => {
    const ids = selectedCards.map((card) => card.id);
    if (!columnId || !ids.length) return;

    setMovingColumn(true);
    Promise.all(ids.map((id) => moveCard(id, columnId, null)))
      .then((results) => {
        dispatch(getColumns(filters));
        dispatch(getCards(filters));

        if (results.some((res) => !res || res.status >= 400)) return;
        setSelected(new Set());
        setColumnModalOpen(false);
      })
      .finally(() => setMovingColumn(false));
  };

  const handleArchive = () => {
    const ids = selectedCards.map((card) => card.id);
    if (!ids.length) return;

    setArchiving(true);
    archiveCards(ids)
      .then((res) => {
        if (!res || res.status >= 400) return;
        setSelected(new Set());
        setArchiveModalOpen(false);
        dispatch(getColumns(filters));
        dispatch(getCards(filters));
      })
      .finally(() => setArchiving(false));
  };

  if (!request.cards.loaded && !request.cards.error) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  if (request.cards.error) {
    return (
      <div className="text-center py-5">
        <p className="text-muted mb-2">Não foi possível carregar as licitações.</p>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => dispatch(getCards(filters))}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <Container ref={printRef}>
      <h5 className="d-none d-print-block mb-3">Gestão IA - Minhas Licitações</h5>

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 d-print-none">
        <label className="mb-0 d-flex align-items-center" htmlFor="select-all-page">
          <input
            id="select-all-page"
            type="checkbox"
            className="mr-2"
            checked={allPageSelected}
            onChange={toggleAllPage}
          />
          Selecionar todas as licitações da página
        </label>

        <div className="d-flex position-relative">
          <div className="mr-2">
            <AiManagementCardMenu
              ariaLabel="Ações em massa"
              label="Ações"
              disabled={selected.size === 0}
              items={[
                { label: 'Alterar coluna', onClick: () => setColumnModalOpen(true) },
                { label: 'Alterar membros', onClick: () => setMembersModalOpen(true) },
                { label: 'Arquivar licitações', onClick: () => setArchiveModalOpen(true) },
              ]}
            />
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm mr-2"
            disabled={selected.size === 0}
            onClick={() => setShowShareWindow((open) => !open)}
          >
            Compartilhar
          </button>

          {showShareWindow && (
            <ItemShare
              setShowShareWindow={setShowShareWindow}
              selectedItems={selectedCards.map((card) => card.bidding_id)}
            />
          )}
          <AiManagementCardMenu
            ariaLabel="Exportar"
            label="Exportar"
            disabled={selected.size === 0}
            items={[
              { label: 'Gerar .xlsx', onClick: () => {} },
              { label: 'Gerar .docx', onClick: () => {} },
              { label: 'Imprimir', onClick: () => print?.() },
            ]}
          />
        </div>
      </div>

      <div className="table-responsive">
        <table className="table list-table">
          <thead>
            <tr>
              <th style={{ width: 40 }} className="d-print-none" aria-label="Selecionar" />
              <th>Edital</th>
              <th>Órgão</th>
              <th>Objeto</th>
              <th>Prazo/Abertura</th>
              <th>Status</th>
              <th aria-label="Membros" />
              <th style={{ width: 40 }} className="d-print-none" aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-4">
                  Nenhuma licitação encontrada.
                </td>
              </tr>
            )}
            {visibleRows.map(({ card, status, statusColor }) => (
              <AiManagementListRow
                key={card.id}
                card={card}
                status={status}
                statusColor={statusColor}
                selected={selected.has(card.id)}
                onToggle={() => toggleOne(card.id)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="d-print-none">
          <Pagination
            postsPerPage={PAGE_SIZE}
            totalPosts={rows.length}
            currentPage={currentPage}
            paginate={setPage}
            firstAndLastPage
          />
        </div>
      )}

      <AiManagementColumnPickerModal
        title="Alterar coluna"
        columns={targetColumns}
        show={columnModalOpen}
        saving={movingColumn}
        onClose={() => setColumnModalOpen(false)}
        onConfirm={handleChangeColumn}
      />

      <AiManagementMembersBulkModal
        cards={selectedCards}
        show={membersModalOpen}
        onClose={() => setMembersModalOpen(false)}
        onSaved={() => dispatch(getCards(filters))}
      />

      <AiManagementArchiveBulkModal
        count={selectedCards.length}
        show={archiveModalOpen}
        saving={archiving}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={handleArchive}
      />
    </Container>
  );
};

export default AiManagementList;
